import { AuthenticationService, MessageService } from '@philips-internal/oswald-ui-core/lib/services';
import { applicationContext, foundationContext } from '@philips-internal/oswald-ui-foundation';

import { setup as setupAuthentication } from './authentication';
import { setup as setupLocale } from './localization';
import { setup as setupModules } from './modules';
import { setup as setupTheme } from './theme';

// Delay before reloading the application after establishment change
const ESTABLISHMENT_CHANGE_RELOAD_DELAY_MS = 1000;

function isAuthenticationRequest(url: string): boolean {
  return url.includes('/authentication') || url.includes('/refresh');
}

function hasEstablishmentChange(bodyString: string): boolean {
  return bodyString.includes('ext_establishment_id');
}

function getBodyString(body: BodyInit): string | null {
  if (typeof body === 'string') {
    return body;
  }

  if (body instanceof URLSearchParams) {
    return body.toString();
  }

  return null;
}

function shouldInterceptRequest(url: string, body: BodyInit | null | undefined): boolean {
  if (!body) {
    return false;
  }
  
  const bodyString = getBodyString(body);
  if (!bodyString) {
    return false;
  }
  
  return isAuthenticationRequest(url) && hasEstablishmentChange(bodyString);
}

async function handleEstablishmentChangeRequest(
  originalFetch: typeof fetch,
  args: Parameters<typeof fetch>
): Promise<Response> {
  const result = await originalFetch.apply(window, args);
  
  if (result.ok) {
    setTimeout(() => window.location.reload(), ESTABLISHMENT_CHANGE_RELOAD_DELAY_MS);
  }
  
  return result;
}

function setupEstablishmentChangeInterceptor(): void {
  const originalFetch = window.fetch;
  
  window.fetch = async (...args: Parameters<typeof fetch>) => {
    const [url, options] = args;
    
    let urlString: string;
    if (typeof url === 'string') {
      urlString = url;
    } else if (url instanceof URL) {
      urlString = url.href;
    } else {
      urlString = String(url);
    }

    if (!shouldInterceptRequest(urlString, options?.body)) {
      return originalFetch.apply(window, args);
    }
    
    try {
      return await handleEstablishmentChangeRequest(originalFetch, args);
    } catch (e) {
      console.error('[Establishment] Error processing establishment change:', e);
      return originalFetch.apply(window, args);
    }
  };
}

async function bootstrap(): Promise<void> {
  applicationContext.setTitle(MessageService.translate('application.name'));

  foundationContext.setCanEditSector(false);
  foundationContext.setCanEditProfile(false);
  foundationContext.setCanEditEstablishment(true);

  setupAuthentication();
  setupModules();
  setupTheme();

  await AuthenticationService.restoreTokenFromStorage();
  
  setupEstablishmentChangeInterceptor();

  return Promise.all(
    [
      setupLocale(),
    ])
    .then(() => Promise.resolve());
}

export {
  bootstrap,
};
